import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, FormControlLabel, Checkbox,
  Switch, Alert, CircularProgress, Tooltip, Divider, ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleIcon from '@mui/icons-material/People';
import BadgeIcon from '@mui/icons-material/Badge';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import secretariaService from '../../services/secretariaService';

const ORANGE = '#F97316';

const CORES_AGENDA = [
  '#3b82f6', '#8b5cf6', '#10b981', '#ef4444', '#f59e0b',
  '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1',
];

const MODULOS = [
  { key: 'dashboard',        label: 'Dashboard',         descricao: 'Visão geral de finanças e indicadores' },
  { key: 'faturamento',      label: 'Faturamento',        descricao: 'Lançar e ver receitas' },
  { key: 'despesas',         label: 'Despesas',           descricao: 'Lançar e ver despesas' },
  { key: 'planoContas',      label: 'Plano de Contas',    descricao: 'Gerenciar categorias financeiras' },
  { key: 'lancamentos',      label: 'Lançamentos',        descricao: 'Extrato de movimentações' },
  { key: 'relatorios',       label: 'Relatórios',         descricao: 'Relatórios financeiros' },
  { key: 'analise',          label: 'Análise IA',         descricao: 'Análise inteligente de dados' },
  { key: 'clinicaDashboard', label: 'Dashboard Clínica',  descricao: 'Indicadores da clínica' },
  { key: 'pacientes',        label: 'Pacientes',          descricao: 'Cadastro e ficha de pacientes' },
  { key: 'procedimentos',    label: 'Procedimentos',      descricao: 'Gerenciar procedimentos' },
  { key: 'agenda',           label: 'Agenda',             descricao: 'Agendamentos e consultas' },
];

const PERMISSOES_PADRAO = MODULOS.reduce((acc, m) => {
  acc[m.key] = m.key === 'agenda' || m.key === 'pacientes';
  return acc;
}, {});

const ModalUsuario = ({ open, onClose, onSalvar, editando }) => {
  const [role, setRole] = useState('secretaria');
  const [form, setForm] = useState({ nome: '', email: '', senha: '', permissoes: PERMISSOES_PADRAO, cor: CORES_AGENDA[0] });
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');

  useEffect(() => {
    if (open && editando) {
      let permissoesObj = PERMISSOES_PADRAO;
      if (editando.permissoes) {
        permissoesObj = typeof editando.permissoes === 'string'
          ? JSON.parse(editando.permissoes)
          : { ...editando.permissoes };
      }
      setRole(editando.role || 'secretaria');
      setForm({
        nome: editando.nome,
        email: editando.email,
        senha: '',
        permissoes: permissoesObj,
        cor: editando.cor || CORES_AGENDA[0],
      });
    } else if (open && !editando) {
      setRole('secretaria');
      setForm({ nome: '', email: '', senha: '', permissoes: PERMISSOES_PADRAO, cor: CORES_AGENDA[0] });
    }
    setErro('');
  }, [editando, open]);

  const handlePermissao = (key) => {
    setForm(f => ({ ...f, permissoes: { ...f.permissoes, [key]: !f.permissoes[key] } }));
  };

  const handleSalvar = async () => {
    setErro('');
    if (!form.nome.trim()) return setErro('Nome é obrigatório');
    if (!form.email.trim()) return setErro('E-mail é obrigatório');
    if (!editando && !form.senha.trim()) return setErro('Senha é obrigatória');

    setLoading(true);
    try {
      const dados = { nome: form.nome, permissoes: form.permissoes, role, cor: role === 'dentista_parceiro' ? form.cor : null };
      if (!editando) { dados.email = form.email; dados.senha = form.senha; }
      if (editando && form.senha) dados.senha = form.senha;
      await onSalvar(dados);
      onClose();
    } catch (err) {
      setErro(err?.response?.data?.message || 'Erro ao salvar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
        {editando ? 'Editar Usuário' : 'Novo Usuário'}
      </DialogTitle>
      <DialogContent>
        {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

        {/* Tipo de usuário */}
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Tipo de usuário</Typography>
        <ToggleButtonGroup
          value={role}
          exclusive
          onChange={(_, v) => { if (v) setRole(v); }}
          size="small"
          sx={{ mb: 2.5, width: '100%' }}
        >
          <ToggleButton value="secretaria" sx={{ flex: 1, gap: 1, '&.Mui-selected': { bgcolor: 'rgba(249,115,22,0.12)', color: ORANGE, borderColor: ORANGE } }}>
            <BadgeIcon fontSize="small" /> Secretaria
          </ToggleButton>
          <ToggleButton value="dentista_parceiro" sx={{ flex: 1, gap: 1, '&.Mui-selected': { bgcolor: 'rgba(249,115,22,0.12)', color: ORANGE, borderColor: ORANGE } }}>
            <MedicalServicesIcon fontSize="small" /> Dentista Parceiro
          </ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField label="Nome completo" value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} fullWidth size="small" />
          <TextField label="E-mail" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} fullWidth size="small" disabled={!!editando} />
          <TextField label={editando ? 'Nova senha (deixe em branco para manter)' : 'Senha'} type="password" value={form.senha} onChange={e => setForm(f => ({ ...f, senha: e.target.value }))} fullWidth size="small" />
        </Box>

        {/* Cor da agenda — só para dentista parceiro */}
        {role === 'dentista_parceiro' && (
          <Box sx={{ mt: 2.5 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Cor na agenda</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {CORES_AGENDA.map(cor => (
                <Box
                  key={cor}
                  onClick={() => setForm(f => ({ ...f, cor }))}
                  sx={{
                    width: 32, height: 32, borderRadius: '50%', bgcolor: cor, cursor: 'pointer',
                    border: form.cor === cor ? '3px solid #fff' : '3px solid transparent',
                    outline: form.cor === cor ? `2px solid ${cor}` : 'none',
                    transition: 'all 0.15s',
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        <Divider sx={{ my: 2.5 }} />

        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>Permissões de acesso</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
          {MODULOS.map(m => (
            <Tooltip key={m.key} title={m.descricao} placement="top">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!form.permissoes[m.key]}
                    onChange={() => handlePermissao(m.key)}
                    size="small"
                    sx={{ '&.Mui-checked': { color: ORANGE } }}
                  />
                }
                label={<Typography variant="body2">{m.label}</Typography>}
                sx={{ m: 0 }}
              />
            </Tooltip>
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>Cancelar</Button>
        <Button onClick={handleSalvar} variant="contained" disabled={loading} sx={{ bgcolor: ORANGE, '&:hover': { bgcolor: '#EA580C' } }}>
          {loading ? <CircularProgress size={20} color="inherit" /> : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const GerenciarUsuarios = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const carregar = async () => {
    try {
      const res = await secretariaService.listar();
      setUsuarios(res.secretarias || []);
    } catch (err) {
      console.error('Erro ao carregar usuários:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { carregar(); }, []);

  const handleSalvar = async (dados) => {
    if (editando) {
      await secretariaService.atualizar(editando.id, dados);
    } else {
      await secretariaService.criar(dados);
    }
    await carregar();
  };

  const handleToggleAtivo = async (u) => {
    await secretariaService.atualizar(u.id, { ativo: !u.ativo });
    await carregar();
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    await secretariaService.remover(confirmDelete.id);
    setConfirmDelete(null);
    await carregar();
  };

  const contarPermissoes = (permissoes) =>
    permissoes ? Object.values(permissoes).filter(Boolean).length : 0;

  const labelRole = (role) =>
    role === 'dentista_parceiro' ? 'Dentista Parceiro' : 'Secretaria';

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PeopleIcon sx={{ color: ORANGE, fontSize: 28 }} />
          <Box>
            <Typography variant="h6" fontWeight={700}>Gerenciar Usuários</Typography>
            <Typography variant="body2" color="text.secondary">
              Gerencie secretarias e dentistas parceiros da clínica
            </Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditando(null); setModalOpen(true); }} sx={{ bgcolor: ORANGE, '&:hover': { bgcolor: '#EA580C' } }}>
          Novo Usuário
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress sx={{ color: ORANGE }} />
        </Box>
      ) : usuarios.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center', borderRadius: 3 }}>
          <PeopleIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">Nenhum usuário cadastrado ainda.</Typography>
          <Button variant="outlined" onClick={() => { setEditando(null); setModalOpen(true); }} sx={{ mt: 2, borderColor: ORANGE, color: ORANGE }}>
            Cadastrar primeiro usuário
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Nome</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>E-mail</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Tipo</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Cor agenda</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Permissões</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {usuarios.map(u => (
                <TableRow key={u.id} hover>
                  <TableCell>{u.nome}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={labelRole(u.role)}
                      size="small"
                      icon={u.role === 'dentista_parceiro' ? <MedicalServicesIcon sx={{ fontSize: '14px !important' }} /> : <BadgeIcon sx={{ fontSize: '14px !important' }} />}
                      sx={{ bgcolor: u.role === 'dentista_parceiro' ? 'rgba(139,92,246,0.1)' : 'rgba(249,115,22,0.1)', color: u.role === 'dentista_parceiro' ? '#8b5cf6' : ORANGE, fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell>
                    {u.cor ? (
                      <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: u.cor, border: '2px solid rgba(0,0,0,0.1)' }} />
                    ) : (
                      <Typography variant="caption" color="text.disabled">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={`${contarPermissoes(u.permissoes)} de ${MODULOS.length} módulos`} size="small" sx={{ bgcolor: 'rgba(249,115,22,0.1)', color: ORANGE, fontWeight: 600 }} />
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={!!u.ativo}
                      onChange={() => handleToggleAtivo(u)}
                      size="small"
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ORANGE }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ORANGE } }}
                    />
                    <Typography variant="caption" color={u.ativo ? 'success.main' : 'text.disabled'}>
                      {u.ativo ? 'Ativo' : 'Inativo'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => { setEditando(u); setModalOpen(true); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remover">
                      <IconButton size="small" color="error" onClick={() => setConfirmDelete(u)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ModalUsuario open={modalOpen} onClose={() => setModalOpen(false)} onSalvar={handleSalvar} editando={editando} />

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Remover usuário?</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja remover <strong>{confirmDelete?.nome}</strong>? Ele perderá o acesso ao sistema.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Remover</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GerenciarUsuarios;
